import React, { createContext, useContext } from 'react'
import { Button, LoadingOverlay, Text } from '@mantine/core'

import { CourtReporter } from '@/apiClient'
import { Wall } from '@/components/Wall'
import { useAuth } from '@/hooks/useAuth'
import { useCourtReporterQuery } from '@/queries/courtReporters'
import { useReporterQuery } from '@/queries/reporters'
import { Reporter } from '@/types'

const ReporterContext = createContext<Reporter | null>(null)
const CourtReporterContext = createContext<CourtReporter | null>(null)

export const UserProvider: React.FC = ({ children }) => {
  const { user } = useAuth()

  if (user?.role === 'reporter') {
    return <ReporterProvider>{children}</ReporterProvider>
  }

  if (user?.role === 'court_reporter') {
    return <CourtReporterProvider>{children}</CourtReporterProvider>
  }

  if (user?.role === 'admin') {
    return <>{children}</>
  }

  if (user) {
    return <NoAccessWall />
  }

  return <LoadingOverlay visible />
}

// A user without a role has no routes here.
const NoAccessWall: React.FC = () => {
  const { logout } = useAuth()

  return (
    <Wall
      heading={
        <Text align="center">
          Your account doesn&apos;t have access to the Reporters App.
        </Text>
      }
    >
      <Button onClick={() => logout({ returnTo: window.location.origin })}>
        Sign out
      </Button>
    </Wall>
  )
}

const AccountLoadErrorWall: React.FC = () => {
  const { logout } = useAuth()

  return (
    <Wall
      heading={
        <Text>
          Sorry, something went wrong, your account couldn't be loaded.
        </Text>
      }
    >
      <Button onClick={() => logout({ returnTo: window.location.origin })}>
        Try again
      </Button>
    </Wall>
  )
}

export const ReporterProvider: React.FC = ({ children }) => {
  const { user } = useAuth()
  const { data: reporter, isLoading, error } = useReporterQuery(user!.id)

  if (error) {
    return <AccountLoadErrorWall />
  }

  if (isLoading || reporter === undefined) {
    return <LoadingOverlay visible />
  }

  return (
    <ReporterContext.Provider value={reporter!}>
      {children}
    </ReporterContext.Provider>
  )
}

export const useReporter = (): Reporter | null => {
  return useContext(ReporterContext)
}

const CourtReporterProvider: React.FC = ({ children }) => {
  const { user } = useAuth()
  const { data: courtReporter, error } = useCourtReporterQuery(user!.id)

  if (error) {
    return <AccountLoadErrorWall />
  }

  if (courtReporter === undefined) {
    return <LoadingOverlay visible />
  }

  return (
    <CourtReporterContext.Provider value={courtReporter}>
      {children}
    </CourtReporterContext.Provider>
  )
}

export const useCourtReporter = (): CourtReporter | null => {
  return useContext(CourtReporterContext)
}
